import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Sidebar = ({
  isCollapsed = false,
  userSubscription = 'free',
  isMobileOpen = false,
  onCloseMobile
}) => {
  const location = useLocation();

  const isSuperAdminRoute = location.pathname.startsWith('/super-admin');

  const userNavigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      premium: false
    },
    {
      label: 'Business Model Canvas',
      path: '/business-model-canvas',
      icon: 'Grid3x3',
      premium: false
    },
    {
      label: 'Predictive Analysis',
      path: '/predictive-analysis',
      icon: 'TrendingUp',
      premium: false
    },
    {
      label: 'Goal Intelligence',
      path: '/goal-intelligence',
      icon: 'Target',
      premium: false
    },
    {
      label: 'Mind Mapping',
      path: '/mind-mapping',
      icon: 'Network',
      premium: false
    },
    {
      label: 'Reports',
      path: '/reports',
      icon: 'FileText',
      premium: true
    },
    {
      label: 'Billing',
      path: '/billing',
      icon: 'CreditCard',
      premium: false
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: 'Settings',
      premium: false
    }
  ];

  const superAdminNavigationItems = [
    {
      label: 'Dashboard',
      path: '/super-admin-dashboard',
      icon: 'LayoutDashboard'
    },
    {
      label: 'Client Management',
      path: '/super-admin/clients',
      icon: 'Users'
    },
    {
      label: 'Subscription & Pricing',
      path: '/super-admin/subscriptions/plans',
      icon: 'CreditCard'
    },
    {
      label: 'Payment Management',
      path: '/super-admin/payments/history',
      icon: 'Wallet'
    },
    {
      label: 'Reports Management',
      path: '/super-admin/reports/generated',
      icon: 'FileBarChart'
    },
    {
      label: 'Content Management',
      path: '/content-management',
      icon: 'FolderKanban'
    },
    {
      label: 'Legal CMS',
      path: '/legal-cms',
      icon: 'Scale'
    }
  ];

  const navigationItems = isSuperAdminRoute
    ? superAdminNavigationItems
    : userNavigationItems;

  const isActive = (path) => {
    if (path === '/super-admin/clients') {
      return location.pathname.startsWith('/super-admin/clients');
    }
    if (path === '/super-admin/subscriptions/plans') {
      return location.pathname.startsWith('/super-admin/subscriptions');
    }
    if (path === '/super-admin/payments/history') {
      return location.pathname.startsWith('/super-admin/payments');
    }
    if (path === '/super-admin/reports/generated') {
      return location.pathname.startsWith('/super-admin/reports');
    }
    return location.pathname === path;
  };

  return (
    <>
      <aside
        className={`
          fixed top-16 left-0 bottom-0 bg-card border-r border-border z-40
          transition-all duration-300
          ${isCollapsed ? 'w-20' : 'w-60'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <div className="flex flex-col h-full">
          <nav className="flex-1 overflow-y-auto py-6 px-4" aria-label="Main navigation">
            <ul className="space-y-2">
              {navigationItems.map((item) => {
                const isPremiumLocked =
                  item.premium && userSubscription === 'free';

                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={onCloseMobile}
                      className={`flex items-center px-4 py-3 rounded-md transition-smooth group relative ${
                        isActive(item.path)
                          ? 'bg-primary text-primary-foreground elevation-sm'
                          : 'text-foreground hover:bg-muted hover:elevation-sm'
                      } ${isPremiumLocked ? 'opacity-60' : ''}`}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <Icon
                        name={item.icon}
                        size={20}
                        color={
                          isActive(item.path)
                            ? 'var(--color-primary-foreground)'
                            : 'currentColor'
                        }
                        strokeWidth={isActive(item.path) ? 2.5 : 2}
                      />

                      {!isCollapsed && (
                        <>
                          <span className="ml-3 text-sm font-medium font-body truncate">
                            {item.label}
                          </span>

                          {isPremiumLocked && (
                            <Icon
                              name="Lock"
                              size={14}
                              color="var(--color-warning)"
                              className="ml-auto shrink-0"
                            />
                          )}
                        </>
                      )}

                      {isCollapsed && isPremiumLocked && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning rounded-full" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="p-4 border-t border-border">
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'px-2'} py-3`}>
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Icon name="User" size={20} color="var(--color-primary)" />
              </div>

              {!isCollapsed && (
                <div className="ml-3 min-w-0">
                  <p className="text-sm font-medium text-foreground font-body truncate">
                    {isSuperAdminRoute ? 'Super Admin' : 'User Account'}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption capitalize">
                    {isSuperAdminRoute ? 'Admin Plan' : `${userSubscription} Plan`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {isMobileOpen && (
        <div
          className="fixed inset-0 top-16 bg-black/40 z-30 lg:hidden"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Sidebar;